import React, { useCallback } from 'react'
import { Radio } from 'antd'
import styled from '@emotion/styled'
import Text from '../../../../components/common/Text'
import { BACKGROUND, FONT_LIGHT_GRAY, INPUT_ERROR, PRIMARY } from '../../../../constant/colors'
import { numberWithCommas } from '../../../../utils/helper'
import { mobile, mobileVerySmall } from '../../../../utils/responsive-helper'
import { InputField } from '../../../../components/common/fields'
import { PreSelectedClaimWrapperList } from '../../../../services/e-claim/e-claim-types'

const RadioLayout = styled.div`
  white-space: normal;
  width: 100%;
  margin-left: 22px;
  .second {
    margin-bottom: 16px;
  }

  ${mobile} {
    margin-left: unset;
    .first {
      align-items: flex-start;
    }
    .second {
      margin-bottom: 8px;
    }
  }
  ${mobileVerySmall} {
    .first {
      flex-wrap: wrap;
      margin-bottom: 6px;
    }
  }
`

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  align-items: center;
`

const Column = styled.div`
  display: flex;
  flex-flow: column;
`

const StyledGroup = styled(Radio.Group)`
  width: 100%;
`
const StyledRadio = styled(Radio)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  min-height: 50px;
  margin-bottom: 8px;
  margin-right: unset;
  padding: 24px 20px;

  border-color: ${BACKGROUND};
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;

  span.ant-radio + * {
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  .ant-radio {
    margin-top: 6px;
    .ant-radio-inner {
      width: 21px;
      height: 21px;
      ::after {
        top: 3px;
        left: 3px;
        width: 13px;
        height: 13px;
      }
    }
  }
  ${mobile} {
    padding: 16px;
    .ant-radio {
      margin-top: 6px;
    }
  }
`

type ChoicesRadioProps<V = BaseOptionType['value']> = {
  value?: V[]
  options: PreSelectedClaimWrapperList[]
  onChange: (value: number) => void
}

const isNumeric = (str: any) => {
  return !isNaN(str)
}

const string2Number = (value: string) => {
  return Number(`${value}`.replaceAll(',', ''))
}

const isMoreMaxValue = (value: string, max: number) =>
  max >= string2Number(value) ? undefined : 'error.moreMaxValue'
const required = (value: string) => (value ? undefined : 'error.required')
const requiredIsMoreZero = (value: string) => (string2Number(value) > 0 ? undefined : 'error.zero')
const requiredIsNumberic = (value: string) =>
  isNumeric(string2Number(value)) ? undefined : 'error.numberic'

const ChoicesRadio = (props: ChoicesRadioProps) => {
  const { value, options, onChange } = props

  return (
    <>
      {options.map((option, index) => (
        <CustomRadio
          key={`${option.claimName}-${option.claimId}-${index}`}
          value={value}
          option={option}
          onChange={onChange}
        />
      ))}
    </>
  )
}
type CustomRadioProps<V = BaseOptionType['value']> = {
  value?: V[]
  option: PreSelectedClaimWrapperList
  onChange: (value: number) => void
}
const CustomRadio = (props: CustomRadioProps) => {
  const { value, option, onChange } = props
  const { maxClaimablePrice } = option

  const validateMoreMax = useCallback(
    (value: string) => {
      return maxClaimablePrice ? isMoreMaxValue(value, maxClaimablePrice) : undefined
    },
    [maxClaimablePrice],
  )

  return (
    <StyledGroup value={value?.includes(option.claimId) ? Number(option.claimId) : undefined}>
      <StyledRadio
        value={option.claimId}
        key={`${option.claimId}-${option.claimName}`}
        onClick={() => {
          onChange(option.claimId)
        }}
        style={{
          borderColor: value?.includes(option.claimId) ? PRIMARY : BACKGROUND,
          borderWidth: value?.includes(option.claimId) ? 2 : 2,
        }}
      >
        <RadioLayout>
          <Row className="first" style={{ whiteSpace: 'unset' }}>
            <Text size={22} type="SemiBold">
              {option.claimName}{' '}
              <span style={{ color: INPUT_ERROR }}>
                {' '}
                ({numberWithCommas(option.maxClaimablePrice || 0)})
              </span>
            </Text>
          </Row>
          {value?.includes(option.claimId) === true && option.claimId && (
            <>
              <Row style={{ marginTop: 12 }}>
                <Text size={18} type="SemiBold" color={FONT_LIGHT_GRAY}>
                  ใส่จำนวนที่ขอเบิก
                </Text>
              </Row>

              <Row>
                <Column>
                  <InputField
                    name={'point_' + option.claimId}
                    placeholder="ใส่จำนวนคะแนน"
                    validate={[required, requiredIsNumberic, validateMoreMax, requiredIsMoreZero]}
                    inputType="number"
                  />
                </Column>
              </Row>
            </>
          )}
        </RadioLayout>
      </StyledRadio>
    </StyledGroup>
  )
}

export default ChoicesRadio
